import { DestroyRef, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Apollo, gql } from 'apollo-angular';
import {
  Observable,
  ReplaySubject,
  Subject,
  catchError,
  debounceTime,
  first,
  groupBy,
  map,
  merge,
  mergeMap,
  of,
  withLatestFrom,
  zip, identity,
  filter,
  tap,
} from 'rxjs';
import { PhAuthService } from '../ph-auth';
import { CurrentUserDocument, CurrentUserModel, CurrentUserQuery } from '@schema';

export type CurrentUser = Omit<CurrentUserModel, '__typename'>;

@Injectable({ providedIn: 'root' })
export class CurrentUserService {
  private _currentUser$ = new ReplaySubject<CurrentUser>(1);
  private _newPreference$: Subject<[string, string | number | boolean]> = new Subject();
  public preferences$: ReplaySubject<{ [key: string]: string | number | boolean }> =
    new ReplaySubject(1);
  constructor(
    private readonly apollo: Apollo,
    private readonly phAuthSrv: PhAuthService,
    private readonly destroyRef: DestroyRef
  ) {
    this.getCurrentUser().pipe(filter(Boolean)).subscribe(this._currentUser$);
    this.currentUser$.pipe(tap(console.log)).subscribe();

    this._newPreference$
      .pipe(
        groupBy(([key]) => key),
        mergeMap((group) => group.pipe(debounceTime(3000)))
      )
      .subscribe(([key, val]) => this._storePermanently(key, val));

    const remotePreferences = this._currentUser$.pipe(map((user) => user.preferences));

    const localPreferences = this._newPreference$.pipe(
      map(([key, value]) => ({ [key]: value })),
      withLatestFrom(this.preferences$),
      map(([newPref, existing]) => ({ ...existing, ...newPref }))
    );

    merge(remotePreferences, localPreferences).pipe(filter(Boolean)).subscribe(this.preferences$);
  }

  public get currentUser$() {
    return this._currentUser$;
  }

  public hasPermissions(permissions: string[]): Observable<boolean> {
    return this.phAuthSrv.scopes$.pipe(
      map((scopes) => permissions.every((p) => scopes.includes(p)))
    );
  }

  public getPreference(key: string) {
    return this.preferences$.pipe(map((x) => x[key]));
  }

  private getCurrentUser() {
    return this.apollo.use('CA')
      .query<CurrentUserQuery>({
        query: CurrentUserDocument,
      })
      .pipe(
        map((res) => {
          if (res?.data?.currentUser) {
            return res.data.currentUser;
          } else {
            return null;
          }
        }),
        catchError((err) => {
          console.error(err);
          return of(null);
        }),
        first()
      );
  }

  save(key: string, value: string | number | boolean) {
    this._newPreference$.next([key, value]);
  }

  _storePermanently(key: string, value?: string | number | boolean) {
    return this.apollo
      .default()
      .mutate<JSON>({
        mutation: gql(`
        mutation($key: String!, $value: JSON) {
          updateUserPreferences(key: $key, value: $value)
        }
      `),
        variables: { key, value },
        fetchPolicy: 'no-cache',
        errorPolicy: 'all',
      })
      .subscribe();
  }
}
